import * as Sentry from "@sentry/react";
import Cookies from "js-cookie";
import { Navigate, useParams } from "react-router-dom";

import { Box, Container, Stack, Typography } from "@mui/material";

import ProviderButton from "@app/analytics/components/ProviderButton";
import useConnectCalendar from "@app/analytics/hooks/calendar/useConnectCalendar";
import CalendarImg from "@app/common/assets/calendar_page.png";
import ArrowVector from "@app/common/assets/purple_arrow_vector.png";
import useAuth from "@app/common/auth/useAuth";
import { CALENDAR_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import ROUTES from "@app/common/routes";
import type { CalendarProviderId } from "@app/common/types/providerTypes";
import Config from "@app/config";
import { usePageTitle } from "@app/utils/hooks";

import Notice from "@rui/Notice";
import ReadLogo from "@rui/ReadLogo";

const getAppUserId = () => encodeURIComponent(Cookies.get("user_cookie") || "");

const SingleCalendarConnect = () => {
  const { providerId } = useParams() as { providerId: CalendarProviderId };
  const provider = CALENDAR_PROVIDERS_BY_ID[providerId];
  const { connectCalendar } = useConnectCalendar();
  usePageTitle(`Connect Calendar ${provider.displayName}`);

  const { user } = useAuth();
  const appUserId = getAppUserId();

  const handleConnectCalendar = (providerId: CalendarProviderId) => {
    const appUserId = getAppUserId();

    const queryParams = appUserId
      ? { encrypted_zoom_user_id: appUserId }
      : undefined;

    const url = appUserId
      ? `${Config.api.HOST}/zoomapp/deeplink`
      : ROUTES.WELCOME_CALENDAR;

    connectCalendar(providerId, {
      returnTo: url,
      action: "welcomeCalendarSuccess",
      errorAction: "calendarError",
      queryParams,
    }).catch(() => {
      // Set to null since we aren't connecting either calendar anymore
      Sentry.captureMessage("failed to connect calendar during onboarding");
    });
  };

  if (!appUserId && user) {
    if (user.userMetadata?.onboardingComplete) {
      return <Navigate to={ROUTES.LANDING_PAGE} />;
    }
    return <Navigate to={ROUTES.WELCOME_CALENDAR} />;
  }

  return (
    <Container>
      <Stack
        sx={{ width: "100%", minHeight: "100vh" }}
        justifyContent="center"
        alignItems="center"
      >
        <Stack
          alignItems="center"
          sx={{
            width: "420px",
            border: "1px solid #DADCE0",
            borderRadius: "4px",
            background: "white",
          }}
        >
          <Stack
            alignItems="center"
            spacing="32px"
            sx={{ padding: "40px 40px 18px" }}
          >
            <ReadLogo />
            <Typography variant="h3" align="center" sx={{ fontWeight: 500 }}>
              To join meetings and provide insights, summaries, transcripts, and
              more, Read needs access to your calendar.
            </Typography>
            <ProviderButton
              sx={{
                width: "100%",
                fontSize: "1.125rem",
                color: "shades.neutral.500",
              }}
              key={provider.id}
              icon={
                <img
                  src={provider.icon}
                  alt={`Connect with ${provider.displayName}`}
                />
              }
              onClick={() => handleConnectCalendar(provider.id)}
              providerId={provider.id}
            >
              Connect with {provider.displayName}
            </ProviderButton>
          </Stack>
          <Stack
            alignItems="center"
            sx={{
              padding: "32px 20px 0",
              minHeight: "300px",
              overflow: "hidden",
            }}
            spacing={1}
          >
            <Notice alignItems="flex-start">
              You're in control: choose which meetings Read joins and change
              your settings at any time.
            </Notice>
            <Box sx={{ width: "100%", position: "relative" }}>
              <Box component="img" src={ArrowVector} sx={{ height: "84px" }} />
              <Box
                component="img"
                src={CalendarImg}
                sx={{ height: "222px", position: "absolute", left: "30px" }}
              />
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};

export default SingleCalendarConnect;
